import React, { useMemo } from 'react';
import { Table, Tag, Tooltip, Button, Popconfirm, Pagination } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { DeleteOutlined } from '@ant-design/icons';
import { Log, LogPagination } from '../../../Modules/Log/Model/Log';
import { extractMainKey, actionMeta } from './helpers';
import dayjs from 'dayjs';
import { getAccountIdById } from '../../../Utils/getAccountNameById';
import { useAppSelector } from '../../../Modules/Presentation/hooks';
import { selectAccounts } from '../../../Modules/Account/Presentation/selectors';
import { selectUser } from '../../../Modules/User/Presentation/selectors';
import { canDeleteLogs, isFullAdmin } from '../../../Utils/limitedPartnerAdmin';
import { parseLogDetails } from '../../../Utils/logDescriptionDetails';

interface Props { 
  data: Log[];
  loading?: boolean;
  pagination?: LogPagination;
  onPageChange?: (page: number, pageSize?: number) => void;
  selected: React.Key[];
  onSelectChange: (selectedRowKeys: React.Key[]) => void;
  onDelete: (logId: number) => void;
}

const fieldLabel: Record<string, string> = {
  main_keyword: '메인 키워드',
  word_keyword: '작업 키워드',
  mid_single: '단일 MID',
  mid_compare: '가격비교 MID',
  product_url: '상품 URL',
  compare_url: '가격비교 URL',
  memo: '메모',
  start_date: '시작일',
  end_date: '종료일',
  status: '상태',
  user: '아이디',
  manager: '생성자',
};

const fieldOrder = Object.keys(fieldLabel);

function sortChangedKeys(keys: string[]): string[] {
  return [...keys].sort((a, b) => {
    const ai = fieldOrder.indexOf(a);
    const bi = fieldOrder.indexOf(b);
    const aKnown = ai !== -1;
    const bKnown = bi !== -1;
    if (aKnown && bKnown) return ai - bi;
    if (aKnown) return -1;
    if (bKnown) return 1;
    return a.localeCompare(b);
  });
}

function renderDetailsTooltip(description: string) {
  const parsed = parseLogDetails(description);
  if (!parsed) return undefined;
  const keys = sortChangedKeys(Object.keys(parsed.changed));
  if (keys.length === 0) return undefined;
  const { changed } = parsed;
  return (
    <div style={{ maxWidth: 420, color: '#fff' }}>
      {keys.map(k => {
        const before = changed[k]?.before ?? '-';
        const after = changed[k]?.after ?? '-';
        const label = fieldLabel[k] || k;
        return (
          <div key={k} style={{ marginBottom: 8 }}>
            <div style={{ fontWeight: 700, color: '#ddd' }}>{label}</div>
            <div style={{ fontSize: 12, color: '#fff', wordBreak: 'break-word' }}>이전(before): {String(before)}</div>
            <div style={{ fontSize: 12, color: '#fff', wordBreak: 'break-word' }}>이후(after): {String(after)}</div>
          </div>
        );
      })}
    </div>
  );
}

const stripLiftSuffix = (username: string): string =>
  username.endsWith('@lift') ? username.slice(0, -5) : username;

const LogTable: React.FC<Props> = ({
  data,
  loading = false,
  pagination,
  onPageChange,
  selected,
  onSelectChange,
  onDelete,
}) => {

  const accountList = useAppSelector(selectAccounts);
  const currentUser = useAppSelector(selectUser);

  const canDelete = canDeleteLogs(currentUser);
  const showWorkerColumn = isFullAdmin(currentUser);

  const columns: ColumnsType<Log> = useMemo(() => {
    const cols: ColumnsType<Log> = [
      {
        title: '행위', dataIndex: 'action', width: 140,
        render: (full: string) => {
          const key = extractMainKey(full);
          const meta = actionMeta[key] ?? { color: 'default', label: full };
          return <Tag color={meta.color}>{meta.label}</Tag>;
        },
      },
      { title: '슬롯 넘버', dataIndex: 'slotId', width: 90 },
      {
        title: '아이디',
        dataIndex: 'sellerId',
        width: 200,
        render: (_: number | null, record) => {
          // API username 우선 사용 (accounts.find 비용 절감)
          const u = record.sellerUsername?.trim();
          if (u) return stripLiftSuffix(u);
          const fallback = record.sellerId ? getAccountIdById(accountList, record.sellerId, true, currentUser) : '-';
          return fallback === '-' ? fallback : stripLiftSuffix(fallback);
        },
      },
      {
        title: '상위 계정',
        dataIndex: 'sellerParentId',
        width: 200,
        render: (_: number | null, record) => {
          const u = record.sellerParentUsername?.trim();
          if (u) return stripLiftSuffix(u);
          const fallback = record.sellerParentId ? getAccountIdById(accountList, record.sellerParentId, true, currentUser) : '-';
          return fallback === '-' ? fallback : stripLiftSuffix(fallback);
        },
      },
      {
        title: '시작일',
        dataIndex: 'start_date',
        width: 120,
        render: (date: string | null) => date ? dayjs(date).format('YYYY-MM-DD') : '-'
      },
      {
        title: '종료일',
        dataIndex: 'end_date',
        width: 120,
        render: (date: string | null) => date ? dayjs(date).format('YYYY-MM-DD') : '-'
      },
      {
        title: '설명', dataIndex: 'description', width: 300,
        render: (text: string) => {
          const tip = renderDetailsTooltip(text);
          const summary = parseLogDetails(text)?.summary ?? String(text ?? '').split('| details=')[0].trim();
          return tip ? (
            <Tooltip
              title={tip}
              trigger="click"
              overlayInnerStyle={{ textAlign: 'left', backgroundColor: '#1f1f1f', color: '#fff' }}
            >
              <span style={{ cursor: 'help' }}>{summary}</span>
            </Tooltip>
          ) : (
            <span>{text}</span>
          );
        }
      },
      { title: '기간', dataIndex: 'total_days', width: 70 },
      { title: '발주일수', dataIndex: 'final_days', width: 70 },
      {
        title: '일시',
        dataIndex: 'created_at',
        width: 180,
        render: (v: string) =>
          dayjs(v).isValid()
            ? dayjs(v).format('YYYY-MM-DD HH:mm:ss')
            : '-',
      },
    ];

    if (showWorkerColumn) {
      cols.push({
        title: '작업자',
        dataIndex: 'actor_username',
        width: 120,
        render: (username: string | null | undefined) => {
          if (!username) return '-';
          return username.endsWith('@lift') ? username.slice(0, -5) : username;
        },
      });
    }

    if (canDelete) {
      cols.push({
        title: '작업',
        width: 80,
        render: (_, record) => (
          <Popconfirm
            title="로그를 삭제하시겠습니까?"
            description="삭제된 로그는 복구할 수 없습니다."
            onConfirm={() => onDelete(record.logId)}
            okText="삭제"
            cancelText="취소"
            okType="danger"
          >
            <Button danger size="small" icon={<DeleteOutlined />} />
          </Popconfirm>
        ),
      });
    }

    return cols;
  }, [accountList, canDelete, currentUser, onDelete, showWorkerColumn]);

  const rowSelection = canDelete ? {
    selectedRowKeys: selected,
    onChange: onSelectChange,
  } : undefined;

  const paginationProps = pagination ? {
    current: pagination.current_page || 1,
    pageSize: pagination.page_size || 20,
    total: pagination.total_count || 0,
  } : undefined;

  return (
    <>
      <Table
        size="small"
        rowKey="logId"
        dataSource={data}
        columns={columns}
        loading={loading}
        rowSelection={rowSelection}
        pagination={false}
        scroll={{ x: 'max-content' }}
        locale={{ emptyText: '조회된 로그가 없습니다.' }}
      />
      {paginationProps && (
        <div style={{ width: '100%', display: 'flex', justifyContent: 'center', padding: '12px 0' }}>
          <Pagination
            current={paginationProps.current}
            pageSize={paginationProps.pageSize}
            total={paginationProps.total}
            showSizeChanger
            pageSizeOptions={['20', '50', '100', '200']}
            showQuickJumper={false}
            showLessItems
            responsive
            showTotal={(total, range) => `${range[0]}-${range[1]} / ${total.toLocaleString()}개 로그`}
            onChange={(page, pageSize) => onPageChange?.(page, pageSize)}
            onShowSizeChange={(_, size) => onPageChange?.(1, size)}
          />
        </div>
      )}
    </>
  );
};

export default React.memo(LogTable);
